import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2, Plus, Lock, Unlock, BookOpen, Trash2, RefreshCw, Search,
  ArrowUpDown, Home, ChevronRight, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, Pencil,
  ParkingCircle, Car, CircleDot, Minus, Landmark, AlertTriangle, History
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API, useProject, useAuth } from "../App";
import { toast } from "sonner";
import { CONFIGURATIONS, FACINGS } from "../constants/dropdownOptions";
import { usePermissions } from "../hooks/usePermissions";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const STATUS_STYLES = {
  available: "bg-emerald-100 text-emerald-700", blocked: "bg-amber-100 text-amber-700",
  booked: "bg-blue-100 text-blue-700", sold: "bg-slate-800 text-white",
};

const SUB_STATUS_LABELS = {
  token_received: "Token Received",
  agreement_pending: "Agreement Pending",
  agreement_done: "Agreement Done",
  registered: "Registered",
  possession_given: "Possession Given"
};

const SUB_STATUS_STYLES = {
  token_received: "bg-blue-50 text-blue-600 border-blue-200",
  agreement_pending: "bg-amber-50 text-amber-600 border-amber-200",
  agreement_done: "bg-emerald-50 text-emerald-600 border-emerald-200",
  registered: "bg-purple-50 text-purple-600 border-purple-200",
  possession_given: "bg-slate-100 text-slate-600 border-slate-300"
};

const MORTGAGE_LABELS = { bank: "Bank", nagar_nigam: "Nagar Nigam" };
const MORTGAGE_STYLES = {
  bank: "bg-orange-100 text-orange-700 border-orange-200",
  nagar_nigam: "bg-rose-100 text-rose-700 border-rose-200",
};

const InventoryPage = () => {
  const { canEdit } = usePermissions();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "ceo";
  const { selectedProjectId, selectedProject } = useProject();
  const [units, setUnits] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterConfig, setFilterConfig] = useState("all");
  const [filterMortgage, setFilterMortgage] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: "", project_name: "", tower: "", floor: 0, unit_number: "",
    configuration: "", carpet_area: 0, super_buildup_area: 0, facing: "",
    parking: 1, base_price: 0, floor_rise: 0, facing_premium: 0, other_charges: 0,
  });
  const [actionOpen, setActionOpen] = useState(false);
  const [actionUnit, setActionUnit] = useState(null);
  const [actionType, setActionType] = useState("");
  const [actionLeadId, setActionLeadId] = useState("");
  const [actionLeadSearch, setActionLeadSearch] = useState("");
  const [actionLeadResults, setActionLeadResults] = useState([]);
  const [actionLeadSelected, setActionLeadSelected] = useState(null);
  const [searchingLeads, setSearchingLeads] = useState(false);

  // Edit state
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState(null);
  const [editUnitOpen, setEditUnitOpen] = useState(false);
  const [editUnitForm, setEditUnitForm] = useState(null);

  // Bulk upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadProjectId, setUploadProjectId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Sub-status state
  const [subStatusOpen, setSubStatusOpen] = useState(false);
  const [subStatusUnit, setSubStatusUnit] = useState(null);
  const [newSubStatus, setNewSubStatus] = useState("");
  const [updatingSubStatus, setUpdatingSubStatus] = useState(false);

  // Mortgage / Lien state (admin only)
  const [mortgageOpen, setMortgageOpen] = useState(false);
  const [mortgageUnit, setMortgageUnit] = useState(null);
  const [mortgageType, setMortgageType] = useState("none");
  const [mortgageNotes, setMortgageNotes] = useState("");
  const [savingMortgage, setSavingMortgage] = useState(false);

  // Booking override reason (when booking a mortgaged unit)
  const [overrideReason, setOverrideReason] = useState("");

  // Parking reservation state (for booking)
  const [parkingPricing, setParkingPricing] = useState(null);
  const [parkingItems, setParkingItems] = useState([]);
  const [parkingSummary, setParkingSummary] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [unitsRes, summaryRes, projRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/summary`),
        axios.get(`${API}/projects`),
      ]);
      setUnits(unitsRes.data);
      setSummary(summaryRes.data);
      setProjects(projRes.data);
    } catch { toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Search leads for block/book action
  const searchLeads = async (query) => {
    if (!query || query.length < 2) {
      setActionLeadResults([]);
      return;
    }
    setSearchingLeads(true);
    try {
      const res = await axios.get(`${API}/leads`, { params: { search: query, limit: 10 } });
      setActionLeadResults(res.data.leads || []);
    } catch (e) {
      console.error("Failed to search leads", e);
      setActionLeadResults([]);
    } finally {
      setSearchingLeads(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (actionLeadSearch) searchLeads(actionLeadSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [actionLeadSearch]);

  const selectLead = (lead) => {
    setActionLeadSelected(lead);
    setActionLeadId(lead.lead_id);
    setActionLeadSearch("");
    setActionLeadResults([]);
  };

  const clearSelectedLead = () => {
    setActionLeadSelected(null);
    setActionLeadId("");
  };

  // Fetch parking data when opening booking dialog
  const openBookingDialog = async (unit) => {
    setActionUnit(unit);
    setActionType("book");
    setActionLeadId("");
    setActionLeadSelected(null);
    setParkingItems([]);
    setActionOpen(true);
    setOverrideReason("");
    
    // Fetch parking pricing and summary for this project
    try {
      const [pricingRes, summaryRes] = await Promise.all([
        axios.get(`${API}/parking/pricing/${unit.project_id}`),
        axios.get(`${API}/parking/summary/${unit.project_id}`),
      ]);
      setParkingPricing(pricingRes.data);
      setParkingSummary(summaryRes.data);
      
      // Initialize parking items with 0 quantity
      const items = [];
      ["open", "covered"].forEach(type => {
        ["single", "tandem", "stack"].forEach(subtype => {
          const priceKey = `${type}_${subtype}`;
          items.push({
            type,
            subtype,
            quantity: 0,
            unit_price: pricingRes.data[priceKey] || 0,
          });
        });
      });
      setParkingItems(items);
    } catch (err) {
      console.error("Failed to fetch parking data:", err);
      // Still allow booking without parking
    }
  };

  const updateParkingQuantity = (type, subtype, delta) => {
    setParkingItems(items => items.map(item => {
      if (item.type === type && item.subtype === subtype) {
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const getParkingTotal = () => {
    return parkingItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const createUnit = async () => {
    try {
      await axios.post(`${API}/inventory`, { ...form, floor: Number(form.floor), parking: Number(form.parking), carpet_area: Number(form.carpet_area), super_buildup_area: Number(form.super_buildup_area), base_price: Number(form.base_price), floor_rise: Number(form.floor_rise), facing_premium: Number(form.facing_premium), other_charges: Number(form.other_charges) });
      toast.success("Unit added");
      setAddOpen(false);
      fetchData();
    } catch { toast.error("Failed to create unit"); }
  };

  const handleAction = async () => {
    if (!actionUnit) return;
    try {
      if (actionType === "block") {
        await axios.post(`${API}/inventory/${actionUnit.unit_id}/block`, { lead_id: actionLeadId });
      } else if (actionType === "book") {
        // First book the unit
        const bookingRes = await axios.post(`${API}/inventory/${actionUnit.unit_id}/book`, { lead_id: actionLeadId, override_reason: overrideReason });
        const bookingId = bookingRes.data?.booking_id;
        
        // Then create parking reservation if any parking selected
        if (bookingId && parkingItems.length > 0) {
          const parkingPayload = {
            booking_id: bookingId,
            lead_id: actionLeadId,
            project_id: actionUnit.project_id,
            unit_number: actionUnit.unit_number,
            items: parkingItems.filter(p => p.quantity > 0).map(p => ({
              type: p.type,
              subtype: p.subtype,
              quantity: p.quantity,
              unit_price: p.unit_price,
              total_price: p.quantity * p.unit_price,
            })),
          };
          
          if (parkingPayload.items.length > 0) {
            await axios.post(`${API}/parking/reservations`, parkingPayload);
          }
        }
      } else if (actionType === "release") {
        await axios.post(`${API}/inventory/${actionUnit.unit_id}/release`);
      }
      toast.success(`Unit ${actionType}ed`);
      setActionOpen(false);
      setActionLeadSelected(null);
      setActionLeadSearch("");
      setActionLeadResults([]);
      setOverrideReason("");
      setParkingItems([]);
      setParkingPricing(null);
      setParkingSummary(null);
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" && detail ? detail.message : detail;
      toast.error(msg || "Action failed");
    }
  };

  const deleteUnit = async (unitId) => {
    try {
      await axios.delete(`${API}/inventory/${unitId}`);
      toast.success("Unit deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const openSubStatus = (unit) => {
    setSubStatusUnit(unit);
    setNewSubStatus(unit.sub_status || "");
    setSubStatusOpen(true);
  };

  const handleSubStatusUpdate = async () => {
    if (!subStatusUnit || !newSubStatus) return;
    setUpdatingSubStatus(true);
    try {
      await axios.put(`${API}/inventory/${subStatusUnit.unit_id}/sub-status`, { sub_status: newSubStatus });
      toast.success(`Sub-status updated to ${SUB_STATUS_LABELS[newSubStatus]}`);
      setSubStatusOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update sub-status");
    } finally {
      setUpdatingSubStatus(false);
    }
  };

  const openMortgage = (unit) => {
    setMortgageUnit(unit);
    setMortgageType(unit.mortgage_type || "none");
    setMortgageNotes("");
    setMortgageOpen(true);
  };

  const saveMortgage = async () => {
    if (!mortgageUnit) return;
    setSavingMortgage(true);
    try {
      const res = await axios.post(`${API}/inventory/${mortgageUnit.unit_id}/mortgage`, {
        mortgage_type: mortgageType === "none" ? null : mortgageType,
        notes: mortgageNotes,
      });
      toast.success("Mortgage status updated");
      setMortgageOpen(false);
      // Keep dialog data fresh if reopened
      setUnits(prev => prev.map(u => u.unit_id === mortgageUnit.unit_id ? res.data.unit : u));
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" && detail ? detail.message : detail;
      toast.error(msg || "Failed to update mortgage");
    } finally {
      setSavingMortgage(false);
    }
  };

  const openEditProject = (proj) => {
    setEditProjectForm({ ...proj, configurations: (proj.configurations || []).join(", "), amenities: (proj.amenities || []).join(", ") });
    setEditProjectOpen(true);
  };

  const saveEditProject = async () => {
    if (!editProjectForm) return;
    try {
      const payload = {
        ...editProjectForm,
        configurations: editProjectForm.configurations.split(",").map(s => s.trim()).filter(Boolean),
        amenities: editProjectForm.amenities.split(",").map(s => s.trim()).filter(Boolean),
        price_range_min: Number(editProjectForm.price_range_min),
        price_range_max: Number(editProjectForm.price_range_max),
        total_units: Number(editProjectForm.total_units),
        available_units: Number(editProjectForm.available_units),
      };
      await axios.put(`${API}/projects/${editProjectForm.project_id}`, payload);
      toast.success("Project updated");
      setEditProjectOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update project"); }
  };

  const openEditUnit = (unit) => {
    setEditUnitForm({ ...unit });
    setEditUnitOpen(true);
  };

  const saveEditUnit = async () => {
    if (!editUnitForm) return;
    try {
      await axios.put(`${API}/inventory/${editUnitForm.unit_id}`, {
        ...editUnitForm,
        carpet_area: Number(editUnitForm.carpet_area),
        super_buildup_area: Number(editUnitForm.super_buildup_area),
        base_price: Number(editUnitForm.base_price),
        floor_rise: Number(editUnitForm.floor_rise),
        facing_premium: Number(editUnitForm.facing_premium),
        other_charges: Number(editUnitForm.other_charges),
        parking: Number(editUnitForm.parking),
        floor: Number(editUnitForm.floor),
      });
      toast.success("Unit updated");
      setEditUnitOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update unit"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/inventory/upload-template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "inventory_template.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const handleBulkUpload = async () => {
    if (!uploadProjectId || !uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("project_id", uploadProjectId);
      const res = await axios.post(`${API}/inventory/bulk-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      if (res.data.inserted_count > 0) {
        toast.success(`${res.data.inserted_count} units imported`);
        fetchData();
      }
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} rows skipped with errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const filtered = units.filter(u => {
    if (selectedProjectId !== "all" && selectedProject && u.project_name !== selectedProject.name) return false;
    if (filterStatus !== "all" && u.status !== filterStatus) return false;
    if (filterConfig !== "all" && u.configuration !== filterConfig) return false;
    if (filterMortgage !== "all") {
      if (filterMortgage === "any" && !u.mortgage_type) return false;
      if (filterMortgage === "none" && u.mortgage_type) return false;
      if (filterMortgage === "bank" && u.mortgage_type !== "bank") return false;
      if (filterMortgage === "nagar_nigam" && u.mortgage_type !== "nagar_nigam") return false;
    }
    if (search && !`${u.tower} ${u.unit_number} ${u.project_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const configs = [...new Set(units.map(u => u.configuration).filter(Boolean))];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="inventory-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Inventory</h1>
            <p className="text-sm text-slate-500 mt-1">Manage units across projects — block, book, track availability</p>
          </div>
          {canEdit("edit_inventory") && (
            <div className="flex gap-2">
              <Button onClick={() => setAddOpen(true)} data-testid="add-unit-btn"><Plus className="w-4 h-4 mr-1" /> Add Unit</Button>
              <Button variant="outline" onClick={() => { setUploadOpen(true); setUploadResult(null); setUploadFile(null); setUploadProjectId(""); }} data-testid="bulk-upload-btn">
                <Upload className="w-4 h-4 mr-1" /> Upload CSV/Excel
              </Button>
            </div>
          )}
        </div>

        {/* Summary */}
        {summary.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.map(s => (
              <Card key={s.project_name}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <p className="font-semibold text-slate-900 mb-2">{s.project_name}</p>
                    {canEdit("edit_projects") && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                        const proj = projects.find(p => p.name === s.project_name);
                        if (proj) openEditProject(proj);
                      }} data-testid={`edit-project-${s.project_name}`}><Pencil className="w-3.5 h-3.5 text-slate-500" /></Button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div><p className="text-slate-500">Available</p><p className="font-bold text-emerald-600 text-lg">{s.available}</p></div>
                    <div><p className="text-slate-500">Blocked</p><p className="font-bold text-amber-600 text-lg">{s.blocked}</p></div>
                    <div><p className="text-slate-500">Booked</p><p className="font-bold text-blue-600 text-lg">{s.booked}</p></div>
                    <div><p className="text-slate-500">Sold</p><p className="font-bold text-slate-700 text-lg">{s.sold}</p></div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Total: {s.total} units | Value: {formatINR(s.value)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search units..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="unit-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="blocked">Blocked</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterConfig} onValueChange={setFilterConfig}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Configs</SelectItem>
              {configs.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterMortgage} onValueChange={setFilterMortgage}>
            <SelectTrigger className="w-[150px]" data-testid="filter-mortgage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Liens</SelectItem>
              <SelectItem value="any">Mortgaged (any)</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
              <SelectItem value="nagar_nigam">Nagar Nigam</SelectItem>
              <SelectItem value="none">Not Mortgaged</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Unit list */}
        {loading ? <div className="text-center py-12 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Loading...</div> :
        filtered.length === 0 ? <Card><CardContent className="p-12 text-center text-slate-400"><Home className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No units found</p></CardContent></Card> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-slate-500">
                  <th className="text-left py-2 font-medium">Unit</th>
                  <th className="text-left py-2 font-medium">Project</th>
                  <th className="text-left py-2 font-medium">Config</th>
                  <th className="text-right py-2 font-medium">Carpet Area</th>
                  <th className="text-right py-2 font-medium">SBU</th>
                  <th className="text-right py-2 font-medium">AUP</th>
                  <th className="text-left py-2 font-medium">Facing</th>
                  <th className="text-right py-2 font-medium">Total Price</th>
                  <th className="text-center py-2 font-medium">Status</th>
                  <th className="text-right py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.unit_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`unit-row-${u.unit_id}`}>
                    <td className="py-2 font-medium text-slate-800">{u.tower}-{u.unit_number} (F{u.floor})</td>
                    <td className="py-2 text-slate-600">{u.project_name}</td>
                    <td className="py-2"><Badge variant="outline" className="text-xs">{u.configuration}</Badge></td>
                    <td className="text-right text-slate-600">{u.carpet_area || "—"}</td>
                    <td className="text-right text-slate-600">{u.super_buildup_area || "—"}</td>
                    <td className="text-right text-slate-600">{u.aup || "—"}</td>
                    <td className="py-2 text-slate-600 capitalize">{u.facing || "—"}</td>
                    <td className="text-right font-semibold text-slate-900">{formatINR(u.total_price)}</td>
                    <td className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Badge className={`text-xs ${STATUS_STYLES[u.status]}`}>{u.status}</Badge>
                        {u.sub_status && (
                          <Badge variant="outline" className={`text-[10px] ${SUB_STATUS_STYLES[u.sub_status] || ""}`} data-testid={`sub-status-${u.unit_id}`}>
                            {SUB_STATUS_LABELS[u.sub_status] || u.sub_status}
                          </Badge>
                        )}
                        {u.mortgage_type && (
                          <Badge variant="outline" className={`text-[10px] ${MORTGAGE_STYLES[u.mortgage_type] || ""}`} data-testid={`mortgage-badge-${u.unit_id}`}>
                            <Landmark className="w-2.5 h-2.5 mr-1" />{MORTGAGE_LABELS[u.mortgage_type]}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Edit and Delete - Admin/Sales Head only */}
                        {canEdit("edit_inventory") && (
                          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => openEditUnit(u)} data-testid={`edit-unit-${u.unit_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {/* Block/Book/Release - Sales Rep can also do these */}
                        {u.status === "available" && (
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { setActionUnit(u); setActionType("block"); setActionLeadId(""); setActionOpen(true); }} data-testid={`block-btn-${u.unit_id}`}>
                            <Lock className="w-3 h-3 mr-1" />Block
                          </Button>
                        )}
                        {(u.status === "available" || u.status === "blocked") && (
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => openBookingDialog(u)} data-testid={`book-btn-${u.unit_id}`}>
                            <BookOpen className="w-3 h-3 mr-1" />Book
                          </Button>
                        )}
                        {u.status !== "sold" && u.status !== "available" && (
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { setActionUnit(u); setActionType("release"); setActionOpen(true); }}>
                            <Unlock className="w-3 h-3 mr-1" />
                          </Button>
                        )}
                        {/* Sub-status button for booked/sold units */}
                        {(u.status === "booked" || u.status === "sold") && (
                          <Button variant="outline" size="sm" className="text-xs h-7 text-purple-600 border-purple-200 hover:bg-purple-50" onClick={() => openSubStatus(u)} data-testid={`sub-status-btn-${u.unit_id}`}>
                            <ArrowUpDown className="w-3 h-3 mr-1" />Status
                          </Button>
                        )}
                        {/* Mortgage/Lien - Admin only */}
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className={`text-xs h-7 ${u.mortgage_type ? "text-orange-600" : "text-slate-400"}`} onClick={() => openMortgage(u)} data-testid={`mortgage-btn-${u.unit_id}`}>
                            <Landmark className="w-3 h-3" />
                          </Button>
                        )}
                        {/* Delete - Admin/Sales Head only */}
                        {canEdit("edit_inventory") && (
                          <Button variant="ghost" size="sm" className="text-xs h-7 text-red-500" onClick={() => deleteUnit(u.unit_id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Unit Dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-unit-dialog">
            <DialogHeader><DialogTitle>Add Inventory Unit</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Project</label>
                <Select value={form.project_id} onValueChange={(v) => {
                  const p = projects.find(p => p.project_id === v);
                  setForm({ ...form, project_id: v, project_name: p?.name || "" });
                }}>
                  <SelectTrigger data-testid="unit-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Tower</label><Input value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} placeholder="A" data-testid="unit-tower" /></div>
                <div><label className="text-sm font-medium mb-1 block">Floor</label><Input type="number" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} data-testid="unit-floor" /></div>
                <div><label className="text-sm font-medium mb-1 block">Unit #</label><Input value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} placeholder="401" data-testid="unit-number" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Configuration</label>
                  <Select value={form.configuration} onValueChange={(v) => setForm({ ...form, configuration: v })}>
                    <SelectTrigger data-testid="unit-config"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {CONFIGURATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><label className="text-sm font-medium mb-1 block">Facing</label>
                  <Select value={form.facing} onValueChange={(v) => setForm({ ...form, facing: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {FACINGS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Carpet (sqft)</label><Input type="number" value={form.carpet_area} onChange={(e) => setForm({ ...form, carpet_area: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Super B/U</label><Input type="number" value={form.super_buildup_area} onChange={(e) => setForm({ ...form, super_buildup_area: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Parking</label><Input type="number" value={form.parking} onChange={(e) => setForm({ ...form, parking: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Base Price</label><Input type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="unit-base-price" /></div>
                <div><label className="text-sm font-medium mb-1 block">Floor Rise</label><Input type="number" value={form.floor_rise} onChange={(e) => setForm({ ...form, floor_rise: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Facing Premium</label><Input type="number" value={form.facing_premium} onChange={(e) => setForm({ ...form, facing_premium: e.target.value })} /></div>
                <div><label className="text-sm font-medium mb-1 block">Other Charges</label><Input type="number" value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={createUnit} data-testid="unit-save-btn">Save Unit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block/Book/Release Dialog */}
        <Dialog open={actionOpen} onOpenChange={(open) => { 
          setActionOpen(open); 
          if (!open) { 
            setActionLeadSelected(null); 
            setActionLeadSearch(""); 
            setActionLeadResults([]); 
            setActionLeadId("");
            setOverrideReason("");
          } 
        }}>
          <DialogContent className="sm:max-w-md" data-testid="action-dialog">
            <DialogHeader><DialogTitle className="capitalize">{actionType} Unit — {actionUnit?.tower}-{actionUnit?.unit_number}</DialogTitle></DialogHeader>
            {actionType !== "release" && (
              <div className="py-2 space-y-3">
                <label className="text-sm font-medium block">Select Lead</label>
                
                {/* Selected lead display */}
                {actionLeadSelected ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{actionLeadSelected.name}</p>
                      <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                        <span>{actionLeadSelected.phone}</span>
                        {actionLeadSelected.email && <span>{actionLeadSelected.email}</span>}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{actionLeadSelected.lead_id}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={clearSelectedLead}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        value={actionLeadSearch} 
                        onChange={(e) => setActionLeadSearch(e.target.value)} 
                        placeholder="Search by name, phone, email, or lead ID..." 
                        className="pl-9"
                        data-testid="action-lead-search" 
                      />
                    </div>
                    
                    {/* Search results dropdown */}
                    {(actionLeadResults.length > 0 || searchingLeads) && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {searchingLeads ? (
                          <div className="p-3 text-center text-slate-400 text-sm">
                            <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1" />
                            Searching...
                          </div>
                        ) : (
                          actionLeadResults.map(lead => (
                            <div 
                              key={lead.lead_id}
                              className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => selectLead(lead)}
                              data-testid={`lead-result-${lead.lead_id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-slate-900">{lead.name}</p>
                                  <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                                    <span>{lead.phone}</span>
                                    {lead.email && <span>{lead.email}</span>}
                                  </div>
                                </div>
                                <Badge variant="outline" className="text-xs">{lead.stage}</Badge>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">{lead.project_interest || "No project"} • {lead.lead_id}</p>
                            </div>
                          ))
                        )}
                        {!searchingLeads && actionLeadResults.length === 0 && actionLeadSearch.length >= 2 && (
                          <div className="p-3 text-center text-slate-400 text-sm">
                            No leads found
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-400 mt-2">Type at least 2 characters to search</p>
                  </div>
                )}
              </div>
            )}

            {/* Mortgage / Lien override warning - Only for Book action on mortgaged units */}
            {actionType === "book" && actionUnit?.mortgage_type && (
              <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-2" data-testid="mortgage-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-orange-800">
                      Unit mortgaged to {MORTGAGE_LABELS[actionUnit.mortgage_type]}
                    </p>
                    <p className="text-xs text-orange-700 mt-0.5">
                      {isAdmin
                        ? "Booking requires an admin override. Provide a reason below — it will be recorded in the audit log."
                        : "Only an admin can override and book a mortgaged unit. Please contact an admin."}
                    </p>
                  </div>
                </div>
                {isAdmin && (
                  <Textarea
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Reason for overriding the lien and booking this unit..."
                    className="bg-white text-sm"
                    rows={2}
                    data-testid="mortgage-override-reason"
                  />
                )}
              </div>
            )}

            {/* Parking Reservation Section - Only for Book action */}
            {actionType === "book" && actionLeadSelected && parkingPricing && (
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <ParkingCircle className="w-4 h-4 text-blue-500" />
                    Parking Reservation
                  </Label>
                  {parkingSummary?.has_over_allocation && (
                    <Badge variant="outline" className="text-amber-600 border-amber-200 text-xs">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Over-allocated
                    </Badge>
                  )}
                </div>

                {/* Open Parking */}
                <div className="bg-sky-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-sky-700 mb-2 flex items-center gap-1">
                    <CircleDot className="w-3 h-3" /> Open Parking
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {["single", "tandem", "stack"].map(subtype => {
                      const item = parkingItems.find(p => p.type === "open" && p.subtype === subtype);
                      const available = parkingSummary?.available?.open?.[subtype] || 0;
                      return (
                        <div key={subtype} className="text-center">
                          <p className="text-xs text-slate-500 capitalize mb-1">{subtype}</p>
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={() => updateParkingQuantity("open", subtype, -1)}
                              disabled={!item || item.quantity === 0}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center font-medium">{item?.quantity || 0}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={() => updateParkingQuantity("open", subtype, 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            ₹{((item?.unit_price || 0) / 100000).toFixed(1)}L • {available} avail
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Covered Parking */}
                <div className="bg-indigo-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-indigo-700 mb-2 flex items-center gap-1">
                    <Car className="w-3 h-3" /> Covered Parking
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {["single", "tandem", "stack"].map(subtype => {
                      const item = parkingItems.find(p => p.type === "covered" && p.subtype === subtype);
                      const available = parkingSummary?.available?.covered?.[subtype] || 0;
                      return (
                        <div key={subtype} className="text-center">
                          <p className="text-xs text-slate-500 capitalize mb-1">{subtype}</p>
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={() => updateParkingQuantity("covered", subtype, -1)}
                              disabled={!item || item.quantity === 0}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center font-medium">{item?.quantity || 0}</span>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={() => updateParkingQuantity("covered", subtype, 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            ₹{((item?.unit_price || 0) / 100000).toFixed(1)}L • {available} avail
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Parking Total */}
                {getParkingTotal() > 0 && (
                  <div className="flex items-center justify-between p-2 bg-slate-100 rounded-lg">
                    <span className="text-sm text-slate-600">Parking Total:</span>
                    <span className="font-semibold text-slate-900">
                      ₹{(getParkingTotal() / 100000).toFixed(2)} L
                    </span>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setActionOpen(false)}>Cancel</Button>
              <Button onClick={handleAction} data-testid="action-confirm-btn" disabled={
                (actionType !== "release" && !actionLeadId) ||
                (actionType === "book" && actionUnit?.mortgage_type && (!isAdmin || !overrideReason.trim()))
              } className="capitalize">{actionType}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bulk-upload-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-['Manrope']">
                <FileSpreadsheet className="w-5 h-5 text-slate-600" />
                Bulk Upload Inventory
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Project selector */}
              <div>
                <label className="text-sm font-medium mb-1.5 block text-slate-700">Project</label>
                <Select value={uploadProjectId} onValueChange={setUploadProjectId}>
                  <SelectTrigger data-testid="upload-project-select">
                    <SelectValue placeholder="Select project for upload" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Format guide */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">File Format Guide</p>
                  <Button variant="ghost" size="sm" className="text-xs h-7 text-blue-600" onClick={downloadTemplate} data-testid="download-template-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download Template
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Upload <strong>.csv</strong> or <strong>.xlsx</strong> with these columns:</p>
                <div className="overflow-x-auto">
                  <table className="text-[10px] w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1 pr-2 text-slate-600 font-semibold">Column</th>
                        <th className="text-left py-1 pr-2 text-slate-600 font-semibold">Required</th>
                        <th className="text-left py-1 text-slate-600 font-semibold">Description</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-500">
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">tower</td><td className="pr-2 text-red-500">Yes</td><td>Tower name (A, B, C...)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">floor</td><td className="pr-2 text-red-500">Yes</td><td>Floor number</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">unit_number</td><td className="pr-2 text-red-500">Yes</td><td>Unit number (401, 702...)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">configuration</td><td className="pr-2 text-red-500">Yes</td><td>1BHK, 2BHK, 3BHK...</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">carpet_area</td><td className="pr-2 text-slate-400">No</td><td>Carpet area (sqft)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">sbu</td><td className="pr-2 text-slate-400">No</td><td>Super Built-Up area (sqft)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">aup</td><td className="pr-2 text-slate-400">No</td><td>Area Under Possession (sqft)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">facing</td><td className="pr-2 text-slate-400">No</td><td>east, west, north, south, park...</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">parking</td><td className="pr-2 text-slate-400">No</td><td>Number of parking slots</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">base_price</td><td className="pr-2 text-slate-400">No</td><td>Base price (INR)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">floor_rise</td><td className="pr-2 text-slate-400">No</td><td>Floor rise charge (INR)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">facing_premium</td><td className="pr-2 text-slate-400">No</td><td>Facing premium (INR)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">other_charges</td><td className="pr-2 text-slate-400">No</td><td>Other charges (INR)</td></tr>
                      <tr><td className="py-0.5 pr-2 font-medium text-slate-700">notes</td><td className="pr-2 text-slate-400">No</td><td>Any notes</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* File picker */}
              <div>
                <label className="text-sm font-medium mb-1.5 block text-slate-700">File</label>
                <Input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  data-testid="upload-file-input"
                  className="cursor-pointer"
                />
                {uploadFile && (
                  <p className="text-xs text-slate-500 mt-1">
                    Selected: <strong>{uploadFile.name}</strong> ({(uploadFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {/* Upload result */}
              {uploadResult && (
                <div className={`rounded-lg p-3 border ${uploadResult.error_count > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="upload-result">
                  <div className="flex items-center gap-2 mb-2">
                    {uploadResult.error_count > 0 ? (
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    )}
                    <p className="text-sm font-medium text-slate-800">
                      {uploadResult.inserted_count} units imported
                      {uploadResult.error_count > 0 && `, ${uploadResult.error_count} rows skipped`}
                    </p>
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
                      {uploadResult.errors.map((e, i) => (
                        <p key={i} className="text-xs text-red-600">Row {e.row}: {e.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadOpen(false)}>Close</Button>
              <Button
                onClick={handleBulkUpload}
                disabled={!uploadProjectId || !uploadFile || uploading}
                data-testid="upload-submit-btn"
              >
                {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload & Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Project Dialog */}
        <Dialog open={editProjectOpen} onOpenChange={setEditProjectOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-project-dialog">
            <DialogHeader><DialogTitle>Edit Project</DialogTitle></DialogHeader>
            {editProjectForm && (
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Project Name</label><Input value={editProjectForm.name || ""} onChange={e => setEditProjectForm({...editProjectForm, name: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Developer</label><Input value={editProjectForm.developer || ""} onChange={e => setEditProjectForm({...editProjectForm, developer: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">RERA ID</label><Input value={editProjectForm.rera_id || ""} onChange={e => setEditProjectForm({...editProjectForm, rera_id: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Locality</label><Input value={editProjectForm.locality || ""} onChange={e => setEditProjectForm({...editProjectForm, locality: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">City</label><Input value={editProjectForm.city || ""} onChange={e => setEditProjectForm({...editProjectForm, city: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Status</label>
                  <Select value={editProjectForm.status || "active"} onValueChange={v => setEditProjectForm({...editProjectForm, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="upcoming">Upcoming</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
                  </Select></div>
                <div><label className="text-sm font-medium mb-1 block">Total Units</label><Input type="number" value={editProjectForm.total_units || 0} onChange={e => setEditProjectForm({...editProjectForm, total_units: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Available Units</label><Input type="number" value={editProjectForm.available_units || 0} onChange={e => setEditProjectForm({...editProjectForm, available_units: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Min Price</label><Input type="number" value={editProjectForm.price_range_min || 0} onChange={e => setEditProjectForm({...editProjectForm, price_range_min: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Max Price</label><Input type="number" value={editProjectForm.price_range_max || 0} onChange={e => setEditProjectForm({...editProjectForm, price_range_max: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Possession Date</label><Input type="date" value={editProjectForm.possession_date || ""} onChange={e => setEditProjectForm({...editProjectForm, possession_date: e.target.value})} /></div>
                <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Configurations (comma separated)</label><Input value={editProjectForm.configurations || ""} onChange={e => setEditProjectForm({...editProjectForm, configurations: e.target.value})} placeholder="1BHK, 2BHK, 3BHK" /></div>
                <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Amenities (comma separated)</label><Input value={editProjectForm.amenities || ""} onChange={e => setEditProjectForm({...editProjectForm, amenities: e.target.value})} placeholder="Pool, Gym, Garden" /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditProjectOpen(false)}>Cancel</Button>
              <Button onClick={saveEditProject} data-testid="save-edit-project-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Unit Dialog */}
        <Dialog open={editUnitOpen} onOpenChange={setEditUnitOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-unit-dialog">
            <DialogHeader><DialogTitle>Edit Unit — {editUnitForm?.unit_number}</DialogTitle></DialogHeader>
            {editUnitForm && (
              <div className="grid grid-cols-2 gap-3 py-2">
                <div><label className="text-sm font-medium mb-1 block">Tower</label><Input value={editUnitForm.tower || ""} onChange={e => setEditUnitForm({...editUnitForm, tower: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Floor</label><Input type="number" value={editUnitForm.floor || 0} onChange={e => setEditUnitForm({...editUnitForm, floor: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Unit #</label><Input value={editUnitForm.unit_number || ""} onChange={e => setEditUnitForm({...editUnitForm, unit_number: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Configuration</label>
                  <Select value={editUnitForm.configuration || ""} onValueChange={v => setEditUnitForm({...editUnitForm, configuration: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CONFIGURATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-sm font-medium mb-1 block">Facing</label>
                  <Select value={editUnitForm.facing || ""} onValueChange={v => setEditUnitForm({...editUnitForm, facing: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FACINGS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-sm font-medium mb-1 block">Carpet (sqft)</label><Input type="number" value={editUnitForm.carpet_area || 0} onChange={e => setEditUnitForm({...editUnitForm, carpet_area: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Super Built-up</label><Input type="number" value={editUnitForm.super_buildup_area || 0} onChange={e => setEditUnitForm({...editUnitForm, super_buildup_area: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Parking</label><Input type="number" value={editUnitForm.parking || 1} onChange={e => setEditUnitForm({...editUnitForm, parking: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Base Price</label><Input type="number" value={editUnitForm.base_price || 0} onChange={e => setEditUnitForm({...editUnitForm, base_price: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Floor Rise</label><Input type="number" value={editUnitForm.floor_rise || 0} onChange={e => setEditUnitForm({...editUnitForm, floor_rise: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Facing Premium</label><Input type="number" value={editUnitForm.facing_premium || 0} onChange={e => setEditUnitForm({...editUnitForm, facing_premium: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Other Charges</label><Input type="number" value={editUnitForm.other_charges || 0} onChange={e => setEditUnitForm({...editUnitForm, other_charges: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Status</label>
                  <Select value={editUnitForm.status || "available"} onValueChange={v => setEditUnitForm({...editUnitForm, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="available">Available</SelectItem><SelectItem value="blocked">Blocked</SelectItem><SelectItem value="booked">Booked</SelectItem><SelectItem value="sold">Sold</SelectItem></SelectContent>
                  </Select></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUnitOpen(false)}>Cancel</Button>
              <Button onClick={saveEditUnit} data-testid="save-edit-unit-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Sub-Status Update Dialog */}
        <Dialog open={subStatusOpen} onOpenChange={setSubStatusOpen}>
          <DialogContent className="sm:max-w-md" data-testid="sub-status-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Update Unit Status</DialogTitle>
            </DialogHeader>
            {subStatusUnit && (
              <div className="space-y-4 py-2">
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-sm font-medium text-slate-800">{subStatusUnit.tower}-{subStatusUnit.unit_number} (F{subStatusUnit.floor})</p>
                  <p className="text-xs text-slate-500 mt-0.5">{subStatusUnit.project_name} &middot; {subStatusUnit.configuration}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={`text-xs ${STATUS_STYLES[subStatusUnit.status]}`}>{subStatusUnit.status}</Badge>
                    {subStatusUnit.sub_status && (
                      <Badge variant="outline" className={`text-[10px] ${SUB_STATUS_STYLES[subStatusUnit.sub_status] || ""}`}>
                        {SUB_STATUS_LABELS[subStatusUnit.sub_status]}
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1.5 block text-slate-700">New Sub-Status</label>
                  <Select value={newSubStatus} onValueChange={setNewSubStatus}>
                    <SelectTrigger data-testid="sub-status-select"><SelectValue placeholder="Select sub-status" /></SelectTrigger>
                    <SelectContent>
                      {subStatusUnit.status === "booked" && (
                        <>
                          <SelectItem value="token_received">Token Received</SelectItem>
                          <SelectItem value="agreement_pending">Agreement Pending</SelectItem>
                          <SelectItem value="agreement_done">Agreement Done</SelectItem>
                        </>
                      )}
                      {subStatusUnit.status === "sold" && (
                        <>
                          <SelectItem value="registered">Registered</SelectItem>
                          <SelectItem value="possession_given">Possession Given</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {newSubStatus === "agreement_done" && subStatusUnit.status === "booked" && (
                    <p className="text-xs text-amber-600 mt-1.5">Note: After agreement is done, you can mark as "Registered" which will move the unit to Sold.</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSubStatusOpen(false)}>Cancel</Button>
              <Button onClick={handleSubStatusUpdate} disabled={!newSubStatus || updatingSubStatus} data-testid="sub-status-confirm-btn">
                {updatingSubStatus ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mortgage / Lien Dialog (Admin only) */}
        <Dialog open={mortgageOpen} onOpenChange={setMortgageOpen}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="mortgage-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-['Manrope']">
                <Landmark className="w-5 h-5 text-orange-600" />
                Mortgage / Lien
              </DialogTitle>
            </DialogHeader>
            {mortgageUnit && (
              <div className="space-y-4 py-2">
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-sm font-medium text-slate-800">{mortgageUnit.tower}-{mortgageUnit.unit_number} (F{mortgageUnit.floor})</p>
                  <p className="text-xs text-slate-500 mt-0.5">{mortgageUnit.project_name} &middot; {mortgageUnit.configuration}</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block text-slate-700">Lien Status</label>
                  <Select value={mortgageType} onValueChange={setMortgageType}>
                    <SelectTrigger data-testid="mortgage-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Mortgaged</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="nagar_nigam">Nagar Nigam</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">A unit can be mortgaged to either a Bank or Nagar Nigam — not both.</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1.5 block text-slate-700">Notes</label>
                  <Textarea
                    value={mortgageNotes}
                    onChange={(e) => setMortgageNotes(e.target.value)}
                    placeholder="Optional note for this change (recorded in history)..."
                    rows={2}
                    className="text-sm"
                    data-testid="mortgage-notes"
                  />
                </div>

                {/* History */}
                {mortgageUnit.mortgage_history?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-1.5 flex items-center gap-1.5 text-slate-700">
                      <History className="w-3.5 h-3.5" /> History
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="mortgage-history">
                      {[...mortgageUnit.mortgage_history].reverse().map((h, i) => (
                        <div key={i} className="text-xs border-l-2 border-slate-200 pl-2 py-0.5">
                          <p className="font-medium text-slate-700 capitalize">
                            {h.event?.replace(/_/g, " ")}
                            {h.mortgage_type && ` → ${MORTGAGE_LABELS[h.mortgage_type] || h.mortgage_type}`}
                          </p>
                          {h.notes && <p className="text-slate-500">{h.notes}</p>}
                          <p className="text-slate-400">{h.user_name} • {h.date ? new Date(h.date).toLocaleString("en-IN") : ""}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMortgageOpen(false)}>Cancel</Button>
              <Button onClick={saveMortgage} disabled={savingMortgage || mortgageType === (mortgageUnit?.mortgage_type || "none")} data-testid="mortgage-save-btn">
                {savingMortgage ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default InventoryPage;
